// noinspection ES6UnusedImports
import React, {useEffect, useState} from 'react';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import ProductMessages from '../message-list';
import Toggle from '@bonprix/pattern-library/templates/toggle/default';
import Select from '@bonprix/pattern-library/templates/input/Select.jsx';
import Option from '@bonprix/pattern-library/templates/input/Option.jsx';
import InputGroup from '@bonprix/pattern-library/templates/input/Group.jsx';
import Device from '@core/scripts/components/device';
import LandmarkAdapter from '@core/scripts/components/landmark-adapter';
import LandmarkHelper from '@core/scripts/helper/landmark-helper';
import NitroEventHelper from '@core/scripts/helper/nitro-event-helper';
import NitroEventFactory from '../../tracking/nitro-event-factory';
import AvailabilityStatusMapper from '../../helper/availability-status-mapper';
import MiniSlider from '@bonprix/pattern-library/templates/slider/MiniSlider.jsx';
import Slide from '@bonprix/pattern-library/templates/slider/Slide.jsx';
import Messages from '@core/scripts/components/messages';
import isBeyondEdgeSlidingEnabled from './selection-utils';
import {Typography} from '@bonprix/design-system';
import {BenefitSectionItemAttributes} from "@product/features/benefitsection/benefit-section-item-attribute.ts";
import {anchorProps, AnchorTarget} from "@product/helper/anchor-utils.ts";

export default function ItemOptionSelection({texts, product, currentItem, currentItemOption, fullWidth}) {
    const [error, setError] = useState(false);
    const [forceUpdate, setForceUpdate] = useState(false);
    const [beyondEdgeSliding, setBeyondEdgeSliding] = useState(isBeyondEdgeSlidingEnabled());

    const sendSwipeLandmark = () => LandmarkAdapter.sendClickLandmark('adp_size_slide');

    useEffect(() => {
        const subscriptions = [
            MessageBroker.subscribe(ProductMessages.ADD_TO_CART_ERROR, ({currentItemOption}) => {
                setError(currentItemOption === null || currentItemOption === undefined);
            }),
            MessageBroker.subscribe(ProductMessages.SELECTION_NEEDED, ({itemOptionNeeded}) => {
                setError(itemOptionNeeded);
            }),
            MessageBroker.subscribe(Messages.BREAKPOINT.CHANGED, () =>
                setBeyondEdgeSliding(isBeyondEdgeSlidingEnabled())
            ),
        ];

        return () =>
            subscriptions.forEach((subscription) => {
                return MessageBroker.unsubscribe(subscription);
            });
    }, []);

    useEffect(() => {
        setForceUpdate(true);
    }, [currentItem]);

    useEffect(() => {
        setError(false);
    }, [currentItemOption]);

    const getActiveIndex = (itemOptions, currentItemOption) => {
        let activeItemIndex = 0;
        itemOptions.find((itemOption, index) => {
            if (currentItemOption?.id === itemOption.id) {
                activeItemIndex = index;
            }
        });
        return activeItemIndex;
    };

    const handleBenefitItemAttributes = (itemOption) => {
        const attributes = [];
        if (!itemOption.parcelShopDelivery) {
            attributes.push(BenefitSectionItemAttributes.PARCEL_SHOP_DELIVERY);
        }
        MessageBroker.publish(ProductMessages.BENEFIT_ITEM_HIDE_IF_MISSING, {attributes});
    };

    const onChangeItemOption = (itemOption) => {
        setForceUpdate(false);
        if (currentItemOption?.id !== itemOption.id) {
            MessageBroker.publish(ProductMessages.CHANGED, {itemOption});

            handleBenefitItemAttributes(itemOption);

            sendItemOptionLandmark(itemOption);
            NitroEventHelper.trigger(
                NitroEventHelper.EventType.DISPLAY_PRODUCT_DETAILS,
                NitroEventFactory.createDisplayProductDetailsEvent(
                    product,
                    product.currentStyle,
                    currentItem,
                    itemOption
                )
            );
        }
    };

    const sendItemOptionLandmark = (itemOption) => {
        const landmarkData = {
            siteType: 'adp',
            selectionType: 'size',
            size: itemOption.name,
            itemNumber: currentItem.itemNumber + currentItem.promotion,
            availability: AvailabilityStatusMapper.mapToAvailabilityStatus(itemOption.availability),
            channelId: product.channelId,
            shopId: product.shopId,
            masterStyleId: product.masterStyleId,
            digiStyleId: product.styleId,
            itemOptionId: itemOption.id,
        };

        LandmarkAdapter.sendClickLandmark(LandmarkHelper.createLandmarkDataString(landmarkData));
    };

    if (currentItemOption?.name === '0') {
        return <div className="product__sizes" test-ref__product-sizes="true" />;
    }

    const calculateItemOptionName = currentItem.hasLongItemOptionName ? () => '' : (itemOptionName) => itemOptionName;
    const sizeValue = currentItemOption ? calculateItemOptionName(currentItemOption.name) : texts.bff_ada_chooseSize;
    const activeIndex = getActiveIndex(currentItem.itemOptions, currentItemOption);
    const isError = error || currentItemOption?.availability?.soldOut;
    const labelValueForLongItemOptionName = isError ? sizeValue : null;

    return (
        <div {...anchorProps(AnchorTarget.SIZE_SELECTION)} className="product__sizes" test-ref__product-sizes="true">
            <span className={fullWidth ? "product__size-selection__full_width" : "product__size-selection"}>
                <Typography variant="body1" component="span" color="text-on-background-mid">
                    {product.lengthInInch ? texts.ppdp__size_inch : texts.ppdp__size}&nbsp;
                </Typography>
                <Typography variant="body1" component="span">
                    {currentItem.hasLongItemOptionName ? labelValueForLongItemOptionName : sizeValue}
                </Typography>
            </span>
            <SelectionInput
                currentItem={currentItem}
                currentItemOption={currentItemOption}
                texts={texts}
                onChangeItemOption={onChangeItemOption}
                error={error}
                sendSwipeLandmark={sendSwipeLandmark}
                activeIndex={activeIndex}
                forceUpdate={forceUpdate}
                beyondEdgeSliding={beyondEdgeSliding}
                fullWidth={fullWidth}
            />
        </div>
    );
}

const SelectionInput = ({
    currentItem,
    currentItemOption,
    texts,
    onChangeItemOption,
    error,
    sendSwipeLandmark,
    activeIndex,
    forceUpdate,
    beyondEdgeSliding,
    fullWidth
}) => {
    const getItemOptionNameWithAvailability = (itemOption) => {
        const name = itemOption.name;
        if (itemOption.availability?.soldOut || itemOption.availability?.tempNotAvailable) {
            return `${name} (${itemOption.availability?.text})`;
        }
        return name;
    };

    if (currentItem.hasLongItemOptionName) {
        let currentItemOptionName;
        if (currentItemOption) {
            currentItemOptionName = getItemOptionNameWithAvailability(currentItemOption);
        }

        return (
            <InputGroup
                hasRows // muss gesetzt sein, da sonst ein <label> tag eingefügt wird, das bei Interaktion mit den options einen Click auf das <input> Element triggert
                testRefs={{'test-ref__product-sizes-select': ''}}
                className={ fullWidth ? "product__sizes-select__full_width" : "product__sizes-select" + (error || currentItemOption?.availability?.soldOut ? ' is-error' : '')
                }
                disabled={currentItem.singleSize}
            >
                <Select
                    custom
                    value={currentItemOption?.id}
                    textValue={currentItemOptionName}
                    testRefs={{'test-ref__product-sizes-select-input': ''}}
                    disabled={currentItem.singleSize}
                    placeholder={!currentItemOption ? texts.bff_ada_chooseSize : null}
                    activeOption={currentItemOption}
                >
                    {currentItem.itemOptions.map((itemOption) => {
                        const name = getItemOptionNameWithAvailability(itemOption);
                        const testRefs = {
                            'test-ref__product-size': AvailabilityStatusMapper.mapToTestAvailabilityStatus(
                                itemOption.availability
                            ),
                            'test-ref__product-size-selected': (
                                currentItemOption && currentItemOption.id === itemOption.id
                            )?.toString(),
                            'test-ref__product-size-text': itemOption.name,
                        };

                        return (
                            <Option
                                custom
                                value={itemOption.id}
                                testRefs={testRefs}
                                onClick={() => onChangeItemOption(itemOption)}
                                icon={itemOption.availability?.tempNotAvailable ? 'email' : null}
                                disabled={itemOption.availability?.soldOut}
                                className="product__sizes-option"
                            >
                                {name}
                            </Option>
                        );
                    })}
                </Select>
            </InputGroup>
        );
    }

    return (
        <MiniSlider
            className={fullWidth ? "product-sizes-slider__full_width" : "product-sizes-slider"}
            isTouch={Device.isTouch() || Device.isApp()}
            activeSlideIndex={beyondEdgeSliding ? activeIndex + 1 : activeIndex}
            onPrevButtonClick={sendSwipeLandmark}
            onNextButtonClick={sendSwipeLandmark}
            onFirstSlide={sendSwipeLandmark}
            testRefs={{'test-ref__product-sizes-slider': ''}}
            beyondEdgeSliding={beyondEdgeSliding}
        >
            {currentItem.itemOptions.map((itemOption, index) => {
                return (
                    <Slide key={index}>
                        <Toggle
                            testRefs={{
                                'test-ref__product-size': AvailabilityStatusMapper.mapToTestAvailabilityStatus(
                                    itemOption.availability
                                ),
                                'test-ref__product-size-selected': (
                                    currentItemOption && currentItemOption.id === itemOption.id
                                )?.toString(),
                                'test-ref__product-size-text': itemOption.name,
                            }}
                            className="product__size"
                            active={currentItemOption && currentItemOption.id === itemOption.id}
                            email={itemOption.availability?.tempNotAvailable ?? false}
                            strikeThrough={itemOption.availability?.soldOut ?? false}
                            text={itemOption.name}
                            onClick={() => onChangeItemOption(itemOption)}
                        />
                    </Slide>
                );
            })}
        </MiniSlider>
    );
};
